/**
 * Centralized Geolocation Service
 * Owns: geolocation fetch (via microservice proxy), localStorage read/write, expiry logic, bot detection.
 * Exposes: window.GeolocationService
 *
 * NOTE: Geolocation is resolved server-side via the microservice (ipapi.co proxy).
 * The provider API key is NOT exposed in the browser — it lives in the shop's
 * Shopify metaobject `site_view.ip_geolocation_api_token` and is read server-side.
 * If that field is empty, the microservice falls back to ipapi.co's free tier
 * (30K requests/month per caller IP) — valid during dev validation, before a
 * paid plan is acquired.
 */
(function () {
    'use strict';

    const STORAGE_KEY = 'userLocationData';
    // 7 days — IP geolocation rarely changes within a week, and stale data here
    // does not affect money path: customer's signed-in default address overrides
    // LS via priority chain, and checkout shipping ZIP is the authoritative input
    // for the gift card claim. Increased from 48h to 7d on 2026-05-22 (RSS-256)
    // to reduce server load on the geolocation API.
    const EXPIRY_MS = 7 * 24 * 60 * 60 * 1000;

    // Microservice geolocation endpoint — token stays server-side
    const BASE_API_URL = (window.theme && window.theme.settings && window.theme.settings.giftCardPoolApiUrl)
        ? window.theme.settings.giftCardPoolApiUrl.replace('/api/energy-rebates/gift-card', '')
        : '';
    const GEOLOCATION_API_URL = BASE_API_URL + '/api/geolocation?shop=' + encodeURIComponent(Shopify.shop);

    // Shared in-flight promise for fetch deduplication
    let _fetchPromise = null;

    /**
     * Returns true if the current user agent looks like a bot/spider.
     * @returns {boolean}
     */
    function isBot() {
        return /bot|spider/i.test(navigator.userAgent);
    }

    /**
     * Returns true if the given expiryTime timestamp is in the past.
     * @param {number} expiryTime
     * @returns {boolean}
     */
    function isExpired(expiryTime) {
        return !expiryTime || Date.now() > expiryTime;
    }

    /**
     * Reads location data from localStorage. Returns the parsed object if
     * valid and not expired, or null otherwise. Removes expired data.
     * @returns {Object|null}
     */
    function getLocationData() {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return null;

            const data = JSON.parse(raw);
            if (data && data.expiryTime && !isExpired(data.expiryTime)) {
                return data;
            }

            // Data is expired — clean it up
            localStorage.removeItem(STORAGE_KEY);
            return null;
        } catch (e) {
            console.warn('GeolocationService: Error reading localStorage:', e);
            return null;
        }
    }

    /**
     * Writes location data to localStorage with an expiry timestamp.
     * If the data object does not already have an expiryTime, one is added.
     * @param {Object} data
     */
    function saveLocationData(data) {
        if (!data) return;
        if (!data.expiryTime) {
            data.expiryTime = Date.now() + EXPIRY_MS;
        }
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        } catch (e) {
            console.warn('GeolocationService: Error writing localStorage:', e);
        }
    }

    /**
     * Transforms a microservice geolocation API response into the standard location data format.
     * @param {Object} apiResponse
     * @returns {Object}
     */
    function createLocationData(apiResponse) {
        return {
            city: apiResponse.city || null,
            region: apiResponse.region || null,
            country: apiResponse.country || null,
            zip: apiResponse.zip || apiResponse.postal || null,
            latitude: apiResponse.latitude ? parseFloat(apiResponse.latitude) : null,
            longitude: apiResponse.longitude ? parseFloat(apiResponse.longitude) : null,
            precise: false,
            source: 'ip',
            expiryTime: Date.now() + EXPIRY_MS
        };
    }

    /**
     * Transforms a Shopify customer default address into the standard location data format.
     * @param {Object} address - Shopify customer default address object
     * @returns {Object|null}
     */
    function getCustomerLocationData(address) {
        if (!address) return null;
        return {
            city: address.city || null,
            region: address.province || null,
            country: address.country || null,
            zip: address.zip || null,
            precise: false,
            source: 'customer',
            expiryTime: Date.now() + EXPIRY_MS
        };
    }

    /**
     * Fetches location from microservice (server-side ipapi.co proxy).
     * Deduplicates concurrent calls — if a fetch is already in-flight, returns the same Promise.
     * On success, saves to localStorage and dispatches 'geolocation:updated' on document.
     * @returns {Promise<Object|null>}
     */
    function fetchLocation() {
        if (_fetchPromise) return _fetchPromise;

        _fetchPromise = fetch(GEOLOCATION_API_URL)
            .then(function (response) {
                if (!response.ok) {
                    throw new Error('Geolocation API request failed. Status: ' + response.status);
                }
                return response.json();
            })
            .then(function (data) {
                if (data && (data.region || data.city)) {
                    const locationData = createLocationData(data);
                    saveLocationData(locationData);

                    document.dispatchEvent(new CustomEvent('geolocation:updated', {
                        detail: { userLocationData: locationData }
                    }));

                    return locationData;
                }
                console.warn('GeolocationService: Incomplete data from geolocation API:', data);
                return null;
            })
            .catch(function (error) {
                console.error('GeolocationService: Error fetching location:', error);
                return null;
            })
            .finally(function () {
                _fetchPromise = null;
            });

        return _fetchPromise;
    }

    // Expose the public API
    window.GeolocationService = {
        STORAGE_KEY: STORAGE_KEY,
        EXPIRY_MS: EXPIRY_MS,
        isBot: isBot,
        isExpired: isExpired,
        getLocationData: getLocationData,
        saveLocationData: saveLocationData,
        fetchLocation: fetchLocation,
        createLocationData: createLocationData,
        getCustomerLocationData: getCustomerLocationData
    };
})();
